import React, { useCallback, useEffect, useState } from "react";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Label } from "reactstrap";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import DateTimePicker from "react-datepicker";
import { format } from "date-fns";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";
import ExportExcel from "../reports/ExportExcel";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import { usePagination } from "../../hooks/usePagination";

const PumpingStatusReport = () => {
  const [fromDateTime, setFromDateTime] = useState<any>(null);
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<any>();
  const [pumpingStationData, setPumpingStationData] = useState<any[]>([]);
  const [selectedStationName, setSelectedStationName] = useState<any>(null);
  const [selectedStationId, setSelectedStationId] = useState<any>(null);
  const [stationDropdownOpen, setStationDropdownOpen] = useState(false);
  const toggleStationDropdown = () => setStationDropdownOpen(!stationDropdownOpen);

  useEffect(() => {
    const getPumpingStations = async () => {
      try {
        const response = await ApiService.axiosApi(`/pumpingstation`, {
          method: "GET",
          params: { pageNumber: 0, pageSize: 0 },
        });
        if (response.success) {
          setPumpingStationData(response.data.data);
          if (response.data.data.length > 0) {
            setSelectedStationName(response.data.data[0].name);
            setSelectedStationId(response.data.data[0].id);
            setId(response.data.data[0].id);
          }
        }
      } catch (error) {
        console.log(error);
      }
    };
    getPumpingStations();
  }, []);

  const {
    data,
    pageNumber,
    setPageNumber,
    pageSize,
    totalCount,
    setRefresh,
    setFromDate,
    setId,
    fromDate,
  } = usePagination({
    endpoint: `/pumpingstation/statushistory`,
    refreshKey,
    enabled: isEnabled,
  });

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleNext = useCallback(() => {
    setPageNumber((prev) => Math.min(totalPages, prev + 1));
  }, [setPageNumber, totalPages]);

  const handlePrev = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, [setPageNumber, totalPages]);

  const validateGenerate = () => {
    if (!selectedStationName) {
      toast.warning("Please select Pumping Station");
      return false;
    }
    if (!fromDateTime) {
      toast.warning("Please select From Date");
      return false;
    }
    return true;
  };

  const handleClick = () => {
    if (!validateGenerate()) return;
    if (fromDateTime) {
      const from = format(fromDateTime, "yyyy-MM-dd HH:mm:ss");
      setFromDate(from);
    }
    setId(selectedStationId);
    setIsEnabled(true);
    setRefresh((prev: any) => !prev);
  };

  const fetchAllDataForExport = async () => {
    try {
      const response = await ApiService.axiosApi(`/pumpingstation/statushistory`, {
        method: "GET",
        params: { FromDate: fromDate, Id: selectedStationId, PageNumber: 0, PageSize: 0 },
      });
      if (response.success) return response.data.data;
      toast.error("Unable to fetch full report");
      return [];
    } catch (error) {
      console.log(error);
      toast.error("API Error");
      return [];
    }
  };

  const updatedData = data?.map((row: any) => ({
    ...row,
    dateTime: format(new Date(row.dateTime), "dd-MM-yyyy HH:mm:ss"),
  }));

  const thGroup: React.CSSProperties = {
    fontWeight: 600, color: "#333", padding: "6px",
    fontSize: "1rem", textAlign: "center",
    borderBottom: "1px solid #dee2e6",
  };
  const thSpan: React.CSSProperties = {
    fontWeight: 600, color: "#333", padding: "6px",
    fontSize: "1rem", height: "50px",
    verticalAlign: "middle", whiteSpace: "nowrap",
  };
  const thSub: React.CSSProperties = {
    fontWeight: 600, color: "#555", padding: "6px 8px",
    fontSize: "0.82rem", whiteSpace: "nowrap", minWidth: "80px",
  };
  const td: React.CSSProperties = { padding: "5px 8px" };

  return (
    <>
      <Breadcrumbs mainTitle="Get Status Report" parent="Pumping Station" />
      <div className="w-100 bg-white px-2 py-2" style={{ overflowX: "hidden" }}>

        {/* Filter Bar */}
        <div
          className="bg-light-primary d-flex justify-content-center align-items-center gap-lg-5"
          style={{ width: "100%", height: "100px", border: "1px solid rgb(43,94,94,0.1)" }}
        >
          {/* Left: Inputs */}
          <div className="d-flex align-items-center gap-lg-5">

            {/* Pumping Station Dropdown */}
            <div className="d-flex flex-column">
              <Label>Pumping Station:</Label>
              <Dropdown isOpen={stationDropdownOpen} toggle={toggleStationDropdown}>
                <DropdownToggle
                  className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                  style={{ fontWeight: 500, borderRadius: "5px", padding: "8px 16px", width: "200px", height: "36px" }}
                  color="whitesmoke"
                >
                  {selectedStationName ?? "Pumping Station"}
                </DropdownToggle>
                <DropdownMenu
                  className="custom-dropdown-menu"
                  style={{ overflowY: "auto", boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)", borderRadius: "8px", padding: "8px 0" }}
                >
                  {pumpingStationData.map((station: any) => (
                    <DropdownItem
                      key={station.id}
                      onClick={() => {
                        setSelectedStationName(station.name);
                        setSelectedStationId(station.id);
                        setId(station.id);
                      }}
                    >
                      {station.name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>

            {/* Date Picker */}
            <div className="report-input-div input-border d-flex flex-column">
              <Label>Date:</Label>
              <DateTimePicker
                className="report-input"
                dateFormat="dd/MM/yyyy"
                placeholderText="DD-MM-YYYY"
               
                onChange={(date: any) => setFromDateTime(date)}
                selected={fromDateTime}
                isClearable
               
              />
            </div>
          </div>

          {/* Right: Buttons */}
          <div className="d-flex align-items-center gap-lg-5">
            <div className="d-flex flex-column">
              <Label style={{ visibility: "hidden" }}>Generate</Label>
              <Button
                color="success"
                onClick={handleClick}
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                Generate
              </Button>
            </div>
            <div className="d-flex flex-column">
              <Label style={{ visibility: "hidden" }}>Export</Label>
              <ExportExcel
                getData={fetchAllDataForExport}
                fileName={"Pumping_Station_Status_Report"}
                hiddenColumns={["id"]}
                EffectiveFrom={fromDate}
              />
            </div>
          </div>
        </div>

        {/* Nested Table */}
        <div className="mt-2" style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <table
            className="table table-sm table-hover text-center"
            style={{ fontSize: "0.95rem", fontFamily: "Segoe UI, Roboto, sans-serif", width: "100%" }}
          >
            <thead className="bg-light-primary">
              {/* Row 1 — group labels */}
              <tr>
                <th rowSpan={2} style={thSpan}>Date &amp; Time</th>

                {[1, 2, 3, 4, 5].map((n) => (
                  <th key={n} colSpan={4} style={thGroup}>Pump {n}</th>
                ))}

                <th colSpan={2} style={thGroup}>Bladder Vessel</th>
                <th rowSpan={2} style={thSpan}>Intake Sump(m)</th>
                <th rowSpan={2} style={thSpan}>Flow Rate(kg/cm)</th>
                <th rowSpan={2} style={thSpan}>Daily Volume (m³)</th>
                <th rowSpan={2} style={thSpan}>Discharge Pressure(bar)</th>
              </tr>

              {/* Row 2 — sub-columns */}
              <tr>
                {[1, 2, 3, 4, 5].map((n) => (
                  <React.Fragment key={n}>
                    <th style={thSub}>Status</th>
                    <th style={thSub}>MBFV Status</th>
                    <th style={thSub}>MBFV Pos(%)</th>
                    <th style={thSub}>Pt(bar)</th>
                  </React.Fragment>
                ))}
                <th style={thSub}>Ton (LC)</th>
                <th style={thSub}>Bar (PT)</th>
              </tr>
            </thead>

            <tbody className="table-body w-100">
              {updatedData && updatedData.length > 0 ? (
                updatedData.map((row: any, idx: number) => (
                  <tr key={row.id ?? idx}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{row.dateTime}</td>

                    {[1, 2, 3, 4, 5].map((n) => (
                      <React.Fragment key={n}>
                        <td style={td}>{row[`pmP${n}_STATUS`]}</td>
                        <td style={td}>{row[`mbfV0${n}_STATUS`]}</td>
                        <td style={td}>{row[`eopD${n}_POS`]}</td>
                        <td style={td}>{row[`pmP${n}_PT_VAL`]}</td>
                      </React.Fragment>
                    ))}

                    <td style={td}>{row.blD_VSL_LC}</td>
                    <td style={td}>{row.blD_VSL_PT}</td>
                    <td style={td}>{row.sumP_LVL_LSP}</td>
                    <td style={td}>{row.maiN_HL_FT_VAL}</td>
                    <td style={td}>{row.maiN_HL_FT_VOL}</td>
                    <td style={td}>{row.maiN_HL_PT_VAL}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={22} className="text-center text-muted py-3">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>

        </div>
     
        {/* Pagination */}
        <div className="d-flex justify-content-end align-content-center" style={{ marginRight: "10px", marginTop: "10px" }}>
          <p style={{ marginRight: "10px", marginTop: "8px" }}>
            Page {pageNumber} of {totalPages || 1}
          </p>
          <button
            onClick={handlePrev}
            disabled={pageNumber === 1}
            className={`icon-button ${pageNumber === 1 ? "disabled" : ""}`}
          >
            <LeftIcon className="icon" />
          </button>
          <button
            onClick={handleNext}
            disabled={pageNumber === totalPages || totalPages === 0}
            className={`icon-button ${pageNumber === totalPages ? "disabled" : ""}`}
          >
            <RightIcon className="icon" />
          </button>
        </div>

      </div>
    </>
  );
};

export default PumpingStatusReport;
